import { useState } from "react";
import { api, type Flag } from "../api.ts";
import { captionStyle, colors, serif, tokens } from "../theme.ts";
import { GlassButton, GlassCard, inputStyle } from "../components/glass.tsx";

// "Not today" hides a flag until tomorrow and records nothing else, so it
// cannot be resolveFlag, which is permanent. Client-side only, in a
// try/catch-wrapped localStorage.
const KEY = (id: string) => `mc2.flagSnooze.${id}`;
const isSnoozedToday = (id: string, today: string) => { try { return localStorage.getItem(KEY(id)) === today; } catch { return false; } };
const snoozeToday = (id: string, today: string) => { try { localStorage.setItem(KEY(id), today); } catch { /* no storage, no memory */ } };

/**
 * One flag, one card. A flag is a decision. When it names its options
 * (0023), the card offers each as a button, then "Not today"; choosing one
 * resolves the flag with that option as the resolution, the same record as
 * typing it. A flag with no named options (every flag made before 0023) keeps
 * the old card: Resolve opens one field for the sentence that settles it.
 */
export function FlagCard({ flag, today, onResolved }: { flag: Flag; today: string; onResolved: () => void }) {
  const [snoozed, setSnoozed] = useState(() => isSnoozedToday(flag.id, today));
  const [resolving, setResolving] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (snoozed) return null;

  const options = (flag.options ?? []).filter((o) => o.trim());

  const settle = async (said: string) => {
    const resolution = said.trim();
    if (!resolution || busy) return;
    setBusy(true); setError(null);
    try { await api.resolveFlag(flag.id, resolution); onResolved(); }
    catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };
  const notToday = () => { snoozeToday(flag.id, today); setSnoozed(true); };

  return (
    <GlassCard padding={16} style={{ marginTop: 16 }}>
      <div style={captionStyle}>Flag · one decision</div>
      <div style={{
        fontFamily: serif, fontSize: tokens.type.sheetTitle.size, lineHeight: tokens.type.sheetTitle.lineHeight,
        margin: "6px 0 10px", color: colors.ink,
      }}>{flag.title}</div>
      {flag.body && <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5, color: colors.ink2 }}>{flag.body}</p>}

      {resolving ? (
        <div>
          <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="what did you decide?" style={inputStyle} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <GlassButton variant="primary" disabled={!text.trim() || busy} onClick={() => settle(text)}>{busy ? "…" : "Save"}</GlassButton>
            <GlassButton variant="ghost" onClick={() => { setResolving(false); setText(""); }}>Cancel</GlassButton>
          </div>
        </div>
      ) : options.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {options.map((o) => (
            <GlassButton key={o} variant="primary" disabled={busy} onClick={() => settle(o)}>{o}</GlassButton>
          ))}
          <GlassButton variant="ghost" onClick={notToday}>Not today</GlassButton>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <GlassButton variant="primary" onClick={() => setResolving(true)}>Resolve</GlassButton>
          <GlassButton variant="ghost" onClick={notToday}>Not today</GlassButton>
        </div>
      )}
      {error && <p style={{ margin: "8px 0 0", fontSize: 12.5, color: colors.ink }}>{error}</p>}
    </GlassCard>
  );
}
